import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Image, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native-web';
import { useRoute } from '@react-navigation/native';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Lottie from 'react-lottie';
import InViewPort from '@coffeebeanslabs/react-native-inviewport';

// Custom Components
import { CateringFeedbackForm } from '../../components/component_catering_feedback_form';
import { Button, NavBar, SceneCateringTagItem } from '../../components';
import DetailTimeSection from '../../components/component_catering_time_section';
import ReminderActionArea from '../../components/component_catering_action_area';

// Lotties
import * as CateringLoadingLottie from '../../lotties/lottie_loading_catering.json';

// Utils
import { showAlert, showAppError, numberWithCommas, formatPhoneNumber } from '../../utils/util_helpers';
import { isFeedbackGiven, isFeedbackNeeded } from '../../utils/util_catering';

// APIs
import {
    getHoodDetail,
    submitCateringFeedback,
    updateHoodReminderStatus,
    updateTruckOrderingOptions
} from '../../modules/api_catering';
import { checkDate } from '../../modules/converters';

// Configs
import GLOBAL from '../../configs/config_global';
import DATA from '../../configs/config_data';
import { getGlobalState, STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';

// Animated Components
const AnimatableScrollView = Animatable.createAnimatableComponent(ScrollView);

export function HoodDetails() {
    const routeParams = useRoute()?.params || {};
    const {
        onFeedbackSubmitted = () => {},
        onConfirmationResponded = () => {}
    } = routeParams;

    // region Refs
    const feedbackForm = useRef(null);
    const orderingOptionsView = useRef(null);
    const mainScrollView = useRef(null);
    // endregion

    // region Global State
    const [globalTruckData] = useGlobalState(STATE_KEYS.TRUCK_DATA);
    // endregion

    // region Local State
    const [hoodData, setHoodData] = useState(routeParams.hoodData);
    const [isLoadingAction, setLoadingAction] = useState(false);
    const [isLoadingDetails, setLoadingDetails] = useState(true);
    const [isSubmittingFeedback, setSubmittingFeedback] = useState(false);
    const [photoCarouselOpened, setPhotoCarouselOpened] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [orderingOptions, setOrderingOptions] = useState([]);
    const [loadingOrderingOptionName, setLoadingOrderingOptionName] = useState('');
    const [orderingOptionViewFlashing, setOrderingOptionViewFlashing] = useState(false);
    const [orderingOptionPosition, setOrderingOptionPosition] = useState(0);
    const [isConfirmedReady, setConfirmedReady] = useState(false);
    const [orderingOptionsViewVisible, setOrderingOptionsViewVisible] = useState(false);

    const [feedbackExpanded, setFeedbackExpanded] = useState(false);
    const [feedbackViewHeight, setFeedbackViewHeight] = useState(0);
    // endregion

    // region Component Mounting
    useEffect(() => {
        setTimeout(() => goFetchHoodDetails(), 1000);
    }, []);
    // endregion

    // region Fetch Hood Detail
    const getCompleteHoodData = (customData) => {
        const usingData = customData || hoodData;
        const thisTruckData = customData?.['truck'].find((truck) => truck.id === globalTruckData.id);
        return Object.assign(cloneDeep(usingData), {
            thisTruckData,
            timeObj: checkDate(hoodData, 'UTC'),
            weather: thisTruckData['weather'],
            confirmed_status: thisTruckData['confirmed_status']
        });
    };

    const goFetchHoodDetails = () => {
        setLoadingDetails(true);
        getHoodDetail(globalTruckData.id, hoodData.id)
            .then((result) => {
                const completeData = getCompleteHoodData(result);
                setHoodData(completeData);
                setOrderingOptions(completeData?.thisTruckData?.['ordering_options'] || []);
                setTimeout(() => goSetConfirmedReady(), 1000);
            })
            .catch((error) => {
                RootNavigation.pop();
                showAppError(error);
            })
            .finally(() => setLoadingDetails(false));
    }
    // endregion

    // region Set Confirmed Ready
    const goSetConfirmedReady = () => {
        setTimeout(() => {
            if (orderingOptionsView.current && !orderingOptionPosition) {
                orderingOptionsView.current.measureLayout(
                    mainScrollView,
                    (ox, oy) => {
                        setOrderingOptionPosition(oy);
                        setConfirmedReady(true);
                    }
                );
            }
            else setConfirmedReady(true);
        }, 500);
    }
    // endregion

    // region Function - Flash Ordering Options
    const flashOrderingOptionView = (noDelay = false) => {
        setTimeout(() => {
            setOrderingOptionViewFlashing(true);

            // Flash off
            setTimeout(() => setOrderingOptionViewFlashing(false), 500);
        }, noDelay ? 250 : 0);
    }
    // endregion

    // region Function - Get Hood Address
    const parseHoodAddress = () => {
        if (!hoodData['location']) return('');

        let usedAddress = hoodData['address'] || hoodData['location']?.['address'] || 'Denver, CO, USA',
            splitAddress = usedAddress.split(','),
            addressLine1 = splitAddress[0]?.trim(),
            addressLine2 = usedAddress.replace(addressLine1 + ',', '').trim(),
            address = (addressLine1.trim() !== addressLine2.trim()) ? (addressLine1 + '\n' + addressLine2) : addressLine1;

        return(address);
    }
    // endregion

    // region Function - Respond to Confirmation
    const respondToConfirmationFunc = (isConfirmed = false, declineReason = '') => {
        setLoadingAction(true);
        updateHoodReminderStatus(globalTruckData.id, hoodData.id, isConfirmed ? 1 : 2, declineReason)
            .then(() => {
                const newHoodData = cloneDeep(hoodData);
                newHoodData['thisTruckData']['confirmed_status'] = isConfirmed ? 1 : 2;

                (!isConfirmed) && showAlert({ text: 'We are now working on finding your replacement. Please contact the Truckster Team.' });

                setHoodData(newHoodData);

                // Callback
                onConfirmationResponded(cloneDeep(newHoodData));
            })
            .catch((error) => showAppError(error))
            .finally(() => setLoadingAction(false));
    }

    const respondToConfirmation = (isConfirmed = false) => {
        if (isConfirmed) {
            // Scroll for vendor to check their ordering options
            if (!orderingOptions.length) {
                mainScrollView.current?.scrollTo({ x: 0, y: orderingOptionPosition, animated: true });
                flashOrderingOptionView();
                return;
            }

            // If still no ordering options was selected
            if (orderingOptions.length === 0) {
                showAlert({
                    text: 'Please select at least one ordering option for your truck at this event.',
                    cancelButton: { text: 'Cancel' },
                    confirmButton: {
                        text: 'OK',
                        onPress: () => flashOrderingOptionView()
                    }
                });
                return;
            }
        }

        // Execute confirmation
        if (isConfirmed) {
            showAlert({
                text: 'You are responding that you will be able to make it to the hood event',
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Confirm',
                    color: GLOBAL.COLOR.MAIN,
                    onPress: () => respondToConfirmationFunc(true)
                }
            });
        }
        else {
            showAlert({
                text: 'Please let us know why not?',
                showTextInput: true,
                cancelButton: { text: 'Cancel' },
                confirmButton: {
                    text: 'Submit',
                    color: GLOBAL.COLOR.MAIN,
                    onPress: (text) => respondToConfirmationFunc(false, text)
                }
            });
        }
    }
    // endregion

    // region Function - Get Given Feedback
    const getGivenFeedback = (customHoodData = null) => {
        const usingHoodData = customHoodData || hoodData;
        const usingHoodDataForThisTruck = usingHoodData?.thisTruckData || {};
        const feedbackGiven = isFeedbackGiven(usingHoodDataForThisTruck);
        return(
            feedbackGiven ?
                {
                    feedbackWeatherText: usingHoodDataForThisTruck['weather'],
                    feedbackSales: usingHoodDataForThisTruck['actual_sales'],
                    invoicingSales: usingHoodDataForThisTruck['invoicing_sales'],
                    feedbackNotes: usingHoodDataForThisTruck['feedback'],
                    feedbackPhotos: usingHoodDataForThisTruck['photos'] || '[]',
                    promoCodeData: usingHoodData['promocode_detail']
                }
                :
                { promoCodeData: usingHoodData['promocode_detail'] }
        );
    }
    // endregion

    // region Function - Submit Feedback
    const executeFeedbackSubmit = ({ salesCount, invoicingSalesCount, feedbackNotes, feedbackWeather, feedbackPhotos }) => {
        setSubmittingFeedback(true);
        submitCateringFeedback(
            globalTruckData?.id,
            hoodData.id,
            salesCount,
            invoicingSalesCount,
            feedbackNotes,
            feedbackWeather,
            feedbackPhotos,
            true
        )
            .then((updatedHoodData) => {
                const newHoodData = getCompleteHoodData(Object.assign(cloneDeep(hoodData), updatedHoodData));
                setHoodData(newHoodData);
                onFeedbackSubmitted(newHoodData);
                showAlert({ text: 'Thank you for your feedback!' });

                // Close feedback form
                feedbackForm.current?.collapseForm();

                // Reload data
                feedbackForm.current?.reloadForm(getGivenFeedback(newHoodData));
            })
            .catch((error) => showAppError(error, () => {}, true))
            .finally(() => setSubmittingFeedback(false))
    }
    // endregion

    // region Render Quick Button
    const renderQuickButton = (text, image, _onPress = () => {}) => {
        const imageSize = 48;
        return(
            <TouchableOpacity
                onPress={_onPress}
                style={{ flex: 1, alignItems: 'center' }}>
                <Image
                    source={image}
                    style={{ width: imageSize, height: imageSize }}
                />

                <Text style={{ fontSize: 15, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginTop: 8, textAlign: 'center' }}>
                    {text}
                </Text>
            </TouchableOpacity>
        );
    }
    // endregion

    // region Render Button Area
    const renderButtonArea = () => {
        const email = hoodData['location']['client_email'], phone = hoodData['location']['client_phone'];
        return(
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', paddingHorizontal: 24, paddingVertical: 24 }}>
                {/* Email */}
                {renderQuickButton(email || 'Email', require('../../images/buttonCircleEmail.png'), () => {
                    if (!email) {
                        showAlert({ text: 'The client did not provide an email address for contact.' });
                        return;
                    }

                    window.open(`mailto:${email}`, '_blank');
                })}

                {/* Call */}
                {renderQuickButton(phone ? (formatPhoneNumber(phone) || phone) : 'Call', require('../../images/buttonCirclePhone.png'), () => {
                    if (!phone) {
                        showAlert({ text: 'The client did not provide a phone number for contact.' });
                        return;
                    }

                    window.open(`tel:${phone}`, '_blank');
                })}
            </View>
        );
    }
    // endregion

    // region Render Address Section
    const renderAddressLineItem = (name = '', detail = '', isFirstItem = false) => (
        <View style={{ alignSelf: 'stretch' }}>
            <Text style={{ fontWeight: '500', marginTop: isFirstItem ? 0 : 24 }}>
                {name}
            </Text>

            <Text
                onPress={isFirstItem ? () => window.open(`https://maps.google.com/?q=${detail.replaceAll('\n', '')}`, '_blank') : undefined}
                style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, lineHeight: 22, marginTop: 8 }}>
                {detail}
            </Text>
        </View>
    )

    const renderAddressSection = () => (
        <View style={{ alignSelf: 'stretch', alignItems: 'flex-start', padding: 24, marginTop: 24, borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
            {/* Detail Items */}
            {renderAddressLineItem(hoodData['location']['name'], parseHoodAddress(), true)}
            {hoodData['location']['parking_instructions'] ? renderAddressLineItem('Specific Parking Instructions', hoodData['location']['parking_instructions']) : false}
            {hoodData['truck_notes'] ? renderAddressLineItem('Notes', hoodData['truck_notes']) : <View/>}
        </View>
    )
    // endregion

    // region Render Truck Ordering Options

    // region Function - Update Ordering Options
    const updateOrderingOptions = (orderingOptionName = '', newOrderingOptions = []) => {
        setLoadingOrderingOptionName(orderingOptionName);
        updateTruckOrderingOptions(hoodData.id, newOrderingOptions)
            .catch((error) => showAppError(error))
            .finally(() => setLoadingOrderingOptionName(''));
    }
    // endregion

    const renderSelectionOption = (text = '') => {
        const isSelected = Array.isArray(orderingOptions) ? orderingOptions.find((item) => item.toLowerCase().trim() === text.toLowerCase().trim()) : false;
        const isLoading = loadingOrderingOptionName.toLowerCase().trim() === text.toLowerCase().trim();

        const isInPast = (hoodData.timeObj.endTime < moment().unix());

        const textMap = {
            'Pre-Order': 'Truckster pre-ordering',
            'Mobile Ordering without Pre-Order': 'Truckster ordering during the event',
            'Walk-up ordering': 'Walk-up ordering'
        };

        return(
            <TouchableOpacity
                disabled={isInPast}
                onPress={() => {
                    if (isLoading) return;
                    const newOrderingOptions = isSelected ? orderingOptions.filter((item) => item.toLowerCase() !== text.toLowerCase()) : orderingOptions.concat(text);
                    setOrderingOptions(newOrderingOptions);
                    updateOrderingOptions(text, newOrderingOptions);
                }}
                style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, opacity: isInPast ? 0.3 : 1 }}>
                {/* Select Indicator */}
                {
                    isLoading ?
                        <ActivityIndicator style={{ width: 24, height: 24 }} color={GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY}/>
                        :
                        <View style={{
                            width: 24, height: 24,
                            borderRadius: 16, borderWidth: 2, borderColor: isSelected ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.TEXT_LIGHT_GRAY,
                            backgroundColor: isSelected ? GLOBAL.COLOR.MAIN : 'white',
                            alignItems: 'center', justifyContent: 'center'
                        }}>
                            {
                                isSelected &&
                                <View style={{ width: 8, height: 8, borderRadius: 6, backgroundColor: 'white' }}/>
                            }
                        </View>
                }

                {/* Text */}
                <Text style={{ flex: 1, marginLeft: 8, color: isLoading ? GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY : GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, fontWeight: isSelected ? '500' : 'normal' }}>
                    {textMap[text] || 'Mobile Ordering'}
                </Text>
            </TouchableOpacity>
        );
    }

    const renderTruckOrderingOptions = () => (
        <InViewPort onChange={(isVisible) => setOrderingOptionsViewVisible(isVisible)}>
            <View
                ref={orderingOptionsView}
                style={{ width: '100%', padding: 24, borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                {/* Flashing View */}
                {
                    orderingOptionViewFlashing &&
                    <View style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0, backgroundColor: GLOBAL.COLOR.BASIC.UI_YELLOW }}/>
                }

                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={{ fontWeight: 'bold', fontSize: 18, marginRight: 8 }}>
                        Your Truck's Ordering Options
                    </Text>

                    {/* Help Button */}
                    {
                        (hoodData.timeObj.endTime > moment().unix()) &&
                        <TouchableOpacity onPress={() => showAlert({ text: 'Select all that apply for your truck at this event.' })}>
                            <Image
                                source={require('../../images/iconLineInfo.png')}
                                style={{ width: 22, height: 22, tintColor: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY }}
                            />
                        </TouchableOpacity>
                    }
                </View>

                {renderSelectionOption('Pre-Order')}
                {renderSelectionOption('Mobile Ordering without Pre-Order')}
                {renderSelectionOption('Walk-up ordering')}
            </View>
        </InViewPort>
    )
    // endregion

    // region Render Attending Trucks
    const renderAttendingTrucks = () => {
        const otherAttendingList = hoodData['truck'].filter((truck) => (truck.id !== hoodData.thisTruckData.id) && (truck['confirmed_status'] !== 2));
        return(
            otherAttendingList.length > 0 ?
                <View style={{ width: '100%', padding: 24, borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                    <Text style={{ fontWeight: 'bold', fontSize: 18 }}>
                        Other Trucks Attending
                    </Text>

                    {
                        otherAttendingList.map((truck) => {
                            const logoSize = 56;
                            return(
                                <View
                                    key={`otherTruck_${truck.id}`}
                                    style={{ flexDirection: 'row', alignItems: 'flex-start', alignSelf: 'stretch', marginTop: 24 }}>
                                    <Image
                                        source={truck['logo'] ? { uri: truck['logo'] } : (truck['cover_photo'] ? { uri: truck['cover_photo'] } : require('../../images/logo_truckster.png'))}
                                        style={{ width: logoSize, height: logoSize }}
                                        resizeMode={truck['logo'] ? 'contain' : (truck['cover_photo'] ? 'cover' : 'contain')}
                                    />

                                    <View style={{ flex: 1, marginLeft: 16 }}>
                                        <Text style={{ fontWeight: '500', fontSize: 16 }}>
                                            {truck.name}
                                        </Text>

                                        <Text style={{ fontSize: 15, color: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY, marginTop: 8, lineHeight: 24 }}>
                                            {truck.cuisine.map((cuisine) => cuisine.name).join(', ')}
                                        </Text>

                                        <Text style={{ fontSize: 15, color: GLOBAL.COLOR.BASIC.NEUTRAL_DARK_GREY, marginTop: 8, lineHeight: 24 }}>
                                            Ordering options:
                                            {'\n'}
                                            • {truck['ordering_options'].length > 0 ? truck['ordering_options'].join('.\n• ') : 'Walk-up Ordering'}.
                                        </Text>
                                    </View>
                                </View>
                            );
                        })
                    }
                </View>
                :
                null
        );
    }
    // endregion

    // region Render Info Item
    const renderInfoItem = (title = '', text = '') => (
        <View style={{ width: '50%' }}>
            <Text style={{ fontWeight: '500', marginTop: 24, fontSize: 16 }}>
                {title}
            </Text>

            <Text style={{ color: GLOBAL.COLOR.BASIC.NEUTRAL_GREY, lineHeight: 26, marginTop: 8, fontSize: 16 }}>
                {text}
            </Text>
        </View>
    )
    // endregion

    // region Render Other Information
    const renderOtherInfo = () => {
        const hoodLocation = hoodData['location'];
        return(
            <View style={{ width: '100%', padding: 24, borderTopWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER }}>
                <Text style={{ fontWeight: 'bold', fontSize: 18 }}>
                    Hood Event Info
                </Text>

                <View style={{ flexDirection: 'row', alignItem: 'flex-start', flexWrap: 'wrap', alignSelf: 'stretch' }}>
                    {renderInfoItem('Minimum Budget', `$${numberWithCommas(hoodData['minimum'] || hoodLocation['minimum'] || 0, false, 2)}`)}
                    {renderInfoItem('Mobile Ordering', !hoodLocation['require_mobile_ordering'] ? 'Optional' : 'Required')}
                    {renderInfoItem('Event Frequency', hoodLocation['frequency'] || 'N/A')}
                    {renderInfoItem('COI Required', hoodLocation['coi_required'] || 'N/A')}
                    {renderInfoItem('Paying', hoodLocation['paying'] || 'N/A')}
                    {renderInfoItem('Invoicing', hoodLocation['invoicing'] || 'N/A')}
                    {renderInfoItem('Tax Exempt', hoodLocation['tax_exempt'] || 'No')}
                </View>

                {/* Instruction Photos */}
                {
                    hoodLocation['vendor_photos']?.length > 0 &&
                    <View style={{ alignSelf: 'stretch', marginTop: 32 }}>
                        {/* Button */}
                        <Button
                            onPress={() => setPhotoCarouselOpened(true)}
                            backgroundColor={'black'}
                            text={'Information Photos'}
                            textColor={'white'}
                            textStyle={{ fontWeight: '500', fontSize: 16 }}
                        />

                        {/* Lightbox Carousel */}
                        <Lightbox
                            plugins={[Captions]}
                            open={photoCarouselOpened}
                            close={() => setPhotoCarouselOpened(false)}
                            index={currentPhotoIndex}
                            on={{ view: ({ index }) => setCurrentPhotoIndex(index) }}
                            slides={hoodLocation['vendor_photos'].map((item, index) => ({
                                key: `imageItem_${item.id}`,
                                src: item['path_webp'] || item['path'],
                                id: item.id,
                                title: `Information Photo ${index + 1} of ${hoodLocation['vendor_photos'].length}`,
                                description: item.caption
                            }))}
                            carousel={{ imageFit: 'contain' }}
                            styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
                        />
                    </View>
                }
            </View>
        );
    }
    // endregion

    // region MAIN RENDER
    const hoodDataForThisTruck = useMemo(() => hoodData?.['thisTruckData'] || {}, [hoodData]);
    const isFuture = hoodData?.timeObj?.startTime >= moment().unix();

    let isConfirmed = hoodDataForThisTruck?.['confirmed_status'] === 1,
        isNotComing = hoodDataForThisTruck?.['confirmed_status'] === 2,
        isReminder =
            !isLoadingDetails &&
            (hoodDataForThisTruck?.['confirmed_status'] === 0) && // haven't responded to confirmation yet
            isFuture, // is not already happening or in the past
        showReminderActionArea = isReminder;

    const feedbackNeeded = useMemo(() => !isLoadingDetails && hoodData && isFeedbackNeeded(hoodDataForThisTruck), [hoodData, isLoadingDetails, hoodDataForThisTruck]);
    const hideContactInfo = !isFuture && DATA.hidePastHoodContactFrom.includes(GLOBAL.DATA.USER.id);

    return(
        <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.MAIN_BACKGROUND }}>
            {/* Nav Bar */}
            <NavBar
                title={'Hood Event Details'}
                leftButtonIconName={'keyboard-arrow-left'}
                leftButtonIconSize={40}
                leftButtonOnPress={() => RootNavigation.pop()}
            />

            {/* Content */}
            {
                isLoadingDetails ?
                    <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Lottie */}
                        <Lottie
                            options={{
                                loop: true,
                                autoplay: true,
                                animationData: CateringLoadingLottie,
                                rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
                            }}
                            width={180}
                            height={180}
                            isStopped={false}
                            isPaused={false}
                        />

                        <Text style={{ color: GLOBAL.COLOR.TEXT_GRAY, fontSize: 16 }}>
                            Loading Hood Event Data
                        </Text>
                    </View>
                    :
                    <Animatable.View animation={'fadeZoom'} style={{ flex: 1, width: '100%' }}>
                        <ScrollView
                            ref={mainScrollView}
                            animation={'fadeZoom'}
                            style={{ flex: 1, width: '100%' }}
                            contentContainerStyle={{ paddingBottom: feedbackViewHeight + 48 }}>
                            <View style={{ width: '70%', alignSelf: 'center', borderWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, backgroundColor: 'white', marginTop: 16 }}>
                                {/* Occasion */}
                                <Text style={{ fontWeight: '900', fontSize: 32, letterSpacing: -0.1, textAlign: 'center', marginHorizontal: 16, marginTop: 24 }}>
                                    {hideContactInfo ? 'Hood Location' : (hoodData['location']?.['category'] || `Hood ID: ${hoodData.id}`)}
                                </Text>

                                {/* Name */}
                                <Text style={{ color: GLOBAL.COLOR.BASIC.BROWNISH_GREY, letterSpacing: 0.5, fontSize: 18, marginTop: 8, lineHeight: 28, textAlign: 'center' }}>
                                    {hideContactInfo ? 'Hood Manager' : (hoodData['location'] ? `Requested by ${hoodData['location']['client_name']}` : '')}
                                </Text>

                                {/* Hire Status */}
                                <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 24 }}>
                                    <View style={{ position: 'absolute', height: 1, left: 0, right: 0, backgroundColor: GLOBAL.COLOR.DIVIDER }}/>

                                    {/* Normal Status */}
                                    <SceneCateringTagItem
                                        style={{ marginRight: 0, marginTop: 0, alignSelf: 'center' }}
                                        text={'Truckster Hood'}
                                        textColor={'white'}
                                        textSize={16}
                                        textWeight={'500'}
                                        backgroundColor={GLOBAL.COLOR.BASIC.UI_GREEN}
                                    />

                                    {/* Confirmation Status */}
                                    {
                                        ((isNotComing || isConfirmed) && !isLoadingDetails) &&
                                        <SceneCateringTagItem
                                            style={{ marginLeft: 8, marginRight: 0, marginTop: 0, alignSelf: 'center' }}
                                            text={isConfirmed ? 'Confirmed' : `Not Going`}
                                            textColor={isConfirmed ? 'white' : GLOBAL.COLOR.BASIC.NEUTRAL_GREY}
                                            textSize={16}
                                            textWeight={'500'}
                                            backgroundColor={isConfirmed ? GLOBAL.COLOR.MAIN : GLOBAL.COLOR.BACKGROUND_GREY}
                                        />
                                    }
                                </View>

                                {/* Main Content */}
                                <Animatable.View animation={'fadeIn'} style={{ flex: 1, width: '100%' }}>
                                    {/* Button Area */}
                                    {(!isNotComing && !hideContactInfo) && renderButtonArea()}

                                    {/* Date Time Section */}
                                    <DetailTimeSection eventData={hoodData} style={{ marginTop: 24 }}/>

                                    {/* Address */}
                                    {hideContactInfo ? <View style={{ height: 24 }}/> : renderAddressSection()}

                                    {/* Ordering Options */}
                                    {isNotComing ? <View/> : renderTruckOrderingOptions()}

                                    {/* Other Attending Trucks */}
                                    {renderAttendingTrucks()}

                                    {/* Other Info */}
                                    {renderOtherInfo()}
                                </Animatable.View>
                            </View>
                        </ScrollView>
                    </Animatable.View>
            }

            {/* Feedback Underlayer */}
            {
                feedbackExpanded &&
                <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />
            }

            {/* Action Area */}
            {
                !isLoadingDetails &&
                <Animatable.View
                    animation={'fadeInUp'} duration={600} delay={500}
                    style={{
                        position: 'absolute', left: '30%', right: '30%', bottom: 24, zIndex: 100,
                        boxShadow: '0px 1px 5px #b0b8b2, 0px -1px 5px #b0b8b2', borderRadius: 16,
                        overflow: 'hidden', backgroundColor: 'white'
                    }}
                    onLayout={(event) => setFeedbackViewHeight(event.nativeEvent.layout.height)}>
                    {/* Feedback Form */}
                    {
                        (feedbackNeeded || isFeedbackGiven(hoodDataForThisTruck)) &&
                        <CateringFeedbackForm
                            ref={feedbackForm}
                            onFeedbackExpanded={(expanded) => setFeedbackExpanded(expanded)}
                            onSubmitFeedback={(feedbackData) => executeFeedbackSubmit(feedbackData)}
                            isSendingFeedback={isSubmittingFeedback}
                            givenFeedback={getGivenFeedback()}
                        />
                    }

                    {/* Reminder Action Area */}
                    {
                        showReminderActionArea &&
                        <ReminderActionArea
                            cateringData={hoodData}
                            text={'Can you make it to the hood event?'}
                            isLoadingAction={isLoadingAction}
                            onConfirm={() => respondToConfirmation(true)}
                            onDecline={() => respondToConfirmation(false)}
                        />
                    }
                </Animatable.View>
            }
        </View>
    );
    // endregion
}
